import { addMinutes } from 'date-fns'
import Vue from 'vue'
import Router from 'vue-router'
import { toHourClock } from '@/utils/date'
import { toMinutes } from '@/utils/time'
import { dropOrigin } from '@/utils/url'
import originRedirect from '@/utils/originRedirect'
import Nest from '@/views/Nest'
import Alert from '@/views/Alert'
import Customer from '@/views/Customer'
import Error from '@/views/Error'
import Login from '@/views/Login'
import SendMail from '@/views/Login/SendMail'
import Complete from '@/views/Login/Complete'
import Registration from '@/views/Login/Registration'
import RegistrationComplete from '@/views/Login/RegistrationComplete'
import Insert from '@/views/MailAddress/Insert'
import PasswordReset from '@/views/Password/Reset'
import ReserveDetail from '@/views/ReserveDetail'
import ReserveEdit from '@/views/ReserveEdit'
import ReserveEditConfirm from '@/views/ReserveEdit/Confirm'
import ReserveEditComplete from '@/views/ReserveEdit/Complete'
import ReserveHistory from '@/views/ReserveHistory'
import ReserveList from '@/views/ReserveList'
import Analysis from '@/views/Analysis'
import AnalysisDashBoard from '@/views/Analysis/DashBoard'
import store from '@/store'

Vue.use(Router)

export default new Router({
  mode: 'history',
  base: BASE_PATH,
  scrollBehavior: (to, from, savedPosition) => savedPosition || { x: 0, y: 0 },
  routes: [
    {
      path: '/sp/login',
      name: 'SP/login',
      component: Login,
      beforeEnter: (to, from, next) => {
        if (['SP/resetMail', 'SP/registration'].includes(from.name)) {
          store.dispatch('login/setLoginData', { password: '' })
        }
        return next()
      },
    },
    {
      path: '/sp/PreRegistration',
      name: 'SP/PreRegistration',
      component: Login,
      beforeEnter: (to, from, next) => {
        store.dispatch('login/setLoginData', { mail: '', password: '', isTemporary: true })
        return next()
      },
    },
    {
      path: '/sp/registration',
      name: 'SP/registration',
      component: Registration,
      beforeEnter: (to, from, next) => {
        if (!store.state.login.login.token) return next({ name: 'SP/login', replace: true })
        return next()
      },
    },
    {
      path: '/sp/registrationComplete',
      name: 'SP/registrationComplete',
      component: RegistrationComplete,
      beforeEnter: (to, from, next) => {
        if (!store.state.login.login.token) return next({ name: 'SP/login', replace: true })
        return next()
      },
    },
    {
      path: '/sp/mailAddress/Insert',
      name: 'Insert',
      component: Insert,
      beforeEnter: async (to, from, next) => {
        if (!to.query.token || !(await store.dispatch('login/regist', to.query.token))) {
          return next({ name: 'SP/login', replace: true })
        }
        return next()
      },
    },
    { path: '/sp/password/resetMail', name: 'SP/resetMail', component: SendMail },
    { path: '/sp/password/resetSend', name: 'SP/resetSend', component: Complete },
    { path: '/sp/password/update', component: PasswordReset },
    { path: '/sp/alert', component: Alert },
    {
      path: '/sp/customer/new',
      name: 'CustomerAdd',
      component: Customer,
      beforeEnter: (to, from, next) => next(addBackTo(to, from)),
      props: ({ query: { backTo } }) => ({ id: 'new', backTo: dropOrigin(backTo) }),
    },
    {
      path: '/sp/customer/:id',
      name: 'CustomerDetail',
      component: Customer,
      beforeEnter: (to, from, next) => next(addBackTo(to, from)),
      props: ({ params: { id, onlyEdit }, query: { backTo } }) => ({
        id: id && +id,
        onlyEdit,
        backTo: dropOrigin(backTo),
      }),
    },
    { path: '/sp/customer', name: 'Customers', component: Customer },
    {
      path: '/sp',
      name: 'ReserveList',
      component: ReserveList,
      props: ({ query }) => ({
        date: query.date && query.date,
      }),
      beforeEnter: (to, from, next) => next(originRedirect()(to)),
    },
    { path: '/sp/reserve/history', component: ReserveHistory },
    {
      path: '/sp/reserve/new',
      component: Nest,
      beforeEnter: (to, from, next) => {
        const route = addBackTo(to, from)
        if (route) return next(route)

        const now = new Date()
        const { date, time } = toHourClock(now.setMinutes(Math.ceil(now.getMinutes() / 15) * 15))
        store.dispatch('reserves/edit/start', {
          adults: 2,
          reserveDatetime: addMinutes(to.query.date || date, toMinutes(time)),
        })

        const { customer_id: customerId } = to.query
        if (customerId) store.dispatch('reserves/edit/attachCustomer', customerId)

        return next()
      },
      children: [
        {
          path: '',
          name: 'ReserveNew',
          component: ReserveEdit,
          props: ({ query }) => ({ backTo: dropOrigin(query.backTo) }),
        },
        {
          path: '/sp/reserve/new/confirm',
          name: 'ReserveNewConfirm',
          component: ReserveEditConfirm,
          beforeEnter: (to, from, next) =>
            store.state.reserves.edit.confirmed ? next() : next({ ...to, name: 'ReserveNew', replace: true }),
          props: ({ query }) => ({ backTo: dropOrigin(query.backTo) }),
        },
      ],
    },
    {
      path: '/sp/reserve/:id',
      name: 'ReserveDetail',
      component: ReserveDetail,
      beforeEnter: (to, from, next) => next(addBackTo(to, from)),
      props: ({ params, query }) => ({
        id: +params.id,
        alertType: query.alert && +query.alert,
        backTo: dropOrigin(query.backTo),
      }),
    },
    {
      path: '/sp/reserve/:id/edit',
      component: Nest,
      beforeEnter: async (to, from, next) => {
        const route = addBackTo(to, from)
        if (route) return next(route)

        store.commit('app/busy', true)
        await store.dispatch('reserves/edit/start', +to.params.id)
        store.commit('app/busy', false)
        return next()
      },
      children: [
        {
          path: '',
          name: 'ReserveEdit',
          component: ReserveEdit,
          props: ({ params, query }) => ({ id: +params.id, backTo: dropOrigin(query.backTo) }),
        },
        {
          path: 'confirm',
          name: 'ReserveEditConfirm',
          component: ReserveEditConfirm,
          props: ({ params, query }) => ({ id: +params.id, backTo: dropOrigin(query.backTo) }),
          beforeEnter: (to, from, next) =>
            store.state.reserves.edit.confirmed ? next() : next({ ...to, name: 'ReserveEdit', replace: true }),
        },
        {
          path: 'complete',
          name: 'ReserveEditComplete',
          component: ReserveEditComplete,
          props: ({ params, query }) => ({ id: +params.id, backTo: dropOrigin(query.backTo) }),
          beforeEnter: (to, from, next) =>
            store.state.reserves.edit.confirmed ? next() : next({ ...to, name: 'ReserveEdit', replace: true }),
        },
      ],
    },
    {
      path: '/sp/error',
      component: Error,
      children: [
        {
          path: '',
          beforeEnter: (to, from, next) => {
            store.state.optional.errorType = 1
            next()
          },
        },
        {
          path: 'maintenance',
          beforeEnter: (to, from, next) => {
            store.state.optional.errorType = 2
            next()
          },
        },
        {
          path: 'stop',
          beforeEnter: (to, from, next) => {
            store.state.optional.errorType = 3
            next()
          },
        },
      ],
    },
    {
      path: '/sp/analysis/',
      component: Analysis,
      children: [
        { path: 'dashboard', name: 'dashboard', component: AnalysisDashBoard },
        { path: '', redirect: 'dashboard' },
        { path: '*', redirect: 'dashboard' },
      ],
    },
    { path: '/sp/*', redirect: { name: 'ReserveList' } },
  ],
})

function addBackTo({ path, query }, { fullPath }) {
  return !query.backTo ? { path, query: { ...query, backTo: fullPath === '/' ? '/sp' : fullPath } } : undefined
}
